"use client";

import {
  createContext,
  type ReactNode,
  Suspense,
  useContext,
  useEffect,
  useState,
} from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useQuery } from "@apollo/client/react";
import { parseAsString, useQueryStates } from "nuqs";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Stack } from "@/components/ui/stack";
import { Typography } from "@/components/ui/typography";

import { graphql } from "@/__generated__/gql";

import {
  groupUniversalSearchResultsByKind,
  UNIVERSAL_SEARCH_KIND_HEADING,
} from "./group-universal-search-results-by-kind";
import {
  isAdminSearchHotkey,
  isModifiedSearchResultClick,
} from "./is-admin-search-hotkey";

const SEARCH_DEBOUNCE_MS = 300;

const ADMIN_UNIVERSAL_SEARCH_DIALOG_QUERY = graphql(`
  query AdminUniversalSearchDialog($query: String!) {
    admin {
      universalSearch(query: $query) {
        kind
        label
        subtitle
        adminPath
      }
    }
  }
`);

type AdminUniversalSearchContextValue = {
  open: boolean;
  setOpen: (open: boolean) => void;
};

const AdminUniversalSearchContext =
  createContext<AdminUniversalSearchContextValue | null>(null);

export function useAdminUniversalSearch(): AdminUniversalSearchContextValue {
  const context = useContext(AdminUniversalSearchContext);
  if (!context) {
    throw new Error(
      "useAdminUniversalSearch must be used within AdminUniversalSearchProvider",
    );
  }
  return context;
}

function AdminUniversalSearchDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const [params] = useQueryStates({
    q: parseAsString.withDefault(""),
  });
  const urlQuery = pathname === "/admin" ? params.q.trim() : "";
  const [searchQuery, setSearchQuery] = useState(urlQuery);
  const [debouncedSearch, setDebouncedSearch] = useState(urlQuery);

  useEffect(() => {
    if (!urlQuery) {
      return;
    }
    onOpenChange(true);
  }, [urlQuery, onOpenChange]);

  useEffect(() => {
    if (!open || !urlQuery) {
      return;
    }
    setSearchQuery(urlQuery);
    setDebouncedSearch(urlQuery);
  }, [open, urlQuery]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timeout);
  }, [searchQuery]);

  const trimmedSearch = debouncedSearch.trim();

  const { data, previousData, loading, error } = useQuery(
    ADMIN_UNIVERSAL_SEARCH_DIALOG_QUERY,
    {
      variables: { query: trimmedSearch },
      skip: !open || !trimmedSearch,
      notifyOnNetworkStatusChange: true,
    },
  );

  const results =
    data?.admin.universalSearch ?? previousData?.admin.universalSearch;
  const isInitialLoading = loading && !results;
  const groupedResults = groupUniversalSearchResultsByKind(results ?? []);

  const handleOpenChange = (nextOpen: boolean) => {
    onOpenChange(nextOpen);
    if (!nextOpen) {
      setSearchQuery("");
      setDebouncedSearch("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="overflow-hidden p-0 sm:max-w-xl">
        <DialogHeader className="sr-only">
          <DialogTitle>Search</DialogTitle>
          <DialogDescription>Search workspaces and returns</DialogDescription>
        </DialogHeader>
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search workspaces and returns…"
            value={searchQuery}
            onValueChange={setSearchQuery}
            className="pr-8"
          />
          <CommandList>
            <CommandEmpty>
              <AdminUniversalSearchEmpty
                searchQuery={trimmedSearch}
                isInitialLoading={isInitialLoading}
                error={error}
              />
            </CommandEmpty>
            {trimmedSearch &&
              !isInitialLoading &&
              !error &&
              groupedResults.map((group) => (
                <CommandGroup
                  key={group.kind}
                  heading={UNIVERSAL_SEARCH_KIND_HEADING[group.kind]}
                >
                  {group.results.map((result) => (
                    <CommandItem
                      key={result.adminPath}
                      value={result.adminPath}
                      onSelect={() => {
                        handleOpenChange(false);
                        router.push(result.adminPath);
                      }}
                    >
                      <Link
                        href={result.adminPath}
                        prefetch={false}
                        tabIndex={-1}
                        className="-mx-2 -my-1.5 min-w-0 flex-1 px-2 py-1.5"
                        onClick={(event) => {
                          if (isModifiedSearchResultClick(event)) {
                            event.stopPropagation();
                            return;
                          }
                          event.preventDefault();
                        }}
                      >
                        <Stack gap={0}>
                          <Typography size="sm">{result.label}</Typography>
                          {result.subtitle ? (
                            <Typography size="xs" emphasis="muted">
                              {result.subtitle}
                            </Typography>
                          ) : null}
                        </Stack>
                      </Link>
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}

function AdminUniversalSearchEmpty({
  searchQuery,
  isInitialLoading,
  error,
}: {
  searchQuery: string;
  isInitialLoading: boolean;
  error?: Error;
}) {
  if (!searchQuery) {
    return "Start typing to search.";
  }

  if (isInitialLoading) {
    return "Searching…";
  }

  if (error) {
    return `Error: ${error.message}`;
  }

  return "No results found.";
}

export function AdminUniversalSearchProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!isAdminSearchHotkey(event)) {
        return;
      }
      event.preventDefault();
      setOpen((current) => !current);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  return (
    <AdminUniversalSearchContext.Provider value={{ open, setOpen }}>
      {children}
      <Suspense fallback={null}>
        <AdminUniversalSearchDialog open={open} onOpenChange={setOpen} />
      </Suspense>
    </AdminUniversalSearchContext.Provider>
  );
}
